import { NgTemplateOutlet } from '@angular/common';
import {
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  OnInit,
  Signal,
  viewChild,
  viewChildren,
  ViewEncapsulation,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { CapturumSharedModule, CapturumTemplateDirective, LocalStorageService, MapItem } from '@capturum/ui/api';
import { CapturumDropdownModule } from '@capturum/ui/dropdown';
import { CapturumLoaderModule, LoadingService } from '@capturum/ui/loader';
import { DEFAULT_LANGUAGE, WINDOW } from '@core/configs/token.config';
import { AppRoutes } from '@core/enums/routes.enum';
import { TenantStatus } from '@core/enums/tenant-status.enum';
import { User } from '@core/interfaces/user.interface';
import { MenuItemTemplatePipe } from '@features/flow-ui-creator/pipes/menu-item-template.pipe';
import { TenantSwitcherService } from '@features/tenant/services/tenant-switcher.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { FeedbackWidgetService } from '@shared/services/feedback-widget.service';
import { NeqtoAuthService } from '@shared/services/neqto-auth.service';
import { PageConfigService } from '@shared/services/page-config.service';
import { TenantRefService } from '@shared/services/tenant-ref.service';
import { MenuItem as CompleteMenuItem } from 'primeng/api';
import { SelectChangeEvent as DropdownChangeEvent } from 'primeng/select';
import { TooltipModule } from 'primeng/tooltip';
import { Popover, PopoverModule } from 'primeng/popover';
import { from, switchMap } from 'rxjs';
import { finalize, first } from 'rxjs/operators';

export interface MenuItem extends CompleteMenuItem {
  requiredPermission?: string;
  clickable?: boolean;
  type?: MenuItemType;
}

export enum MenuItemType {
  Link = 'link',
  Template = 'template',
}

@Component({
  selector: 'app-user-menu',
  templateUrl: './user-menu.component.html',
  styleUrls: ['./user-menu.component.scss'],
  imports: [
    PopoverModule,
    TranslateModule,
    CapturumSharedModule,
    NgTemplateOutlet,
    MenuItemTemplatePipe,
    CapturumDropdownModule,
    ReactiveFormsModule,
    FormsModule,
    CapturumLoaderModule,
    TooltipModule,
  ],
  encapsulation: ViewEncapsulation.None,
})
export class UserMenuComponent implements OnInit {
  menuPanel = viewChild(Popover);
  templates = viewChildren(CapturumTemplateDirective);
  menuItems: MenuItem[];
  user: User;
  mobileSidebarExpanded = false;
  menuType = MenuItemType;
  currentLanguage = 'nl';
  languages: MapItem[] = [];
  form: FormGroup;
  tenantStatus = TenantStatus;
  // Same source as the login selector so pending (INVITED) / INACTIVE tenants show up, shown disabled with a tooltip.
  tenantList: Signal<(MapItem & { disabled: boolean })[]> = computed(() => {
    return this.#tenantRefService
      .getTenantList()()
      .map((tenant) => {
        const status = (tenant as MapItem & { status?: TenantStatus }).status;

        return {
          ...tenant,
          disabled: status === TenantStatus.INACTIVE || status === TenantStatus.INVITED,
        };
      });
  });

  readonly #destroyRef = inject(DestroyRef);
  readonly #router = inject(Router);
  readonly #authService = inject(NeqtoAuthService);
  readonly #pageConfigService = inject(PageConfigService);
  readonly #localStorageService = inject(LocalStorageService);
  readonly #feedbackWidgetService = inject(FeedbackWidgetService);
  readonly #defaultLanguage = inject(DEFAULT_LANGUAGE);
  readonly #translateService = inject(TranslateService);
  readonly #formBuilder = inject(FormBuilder);
  readonly #loadingService = inject(LoadingService);
  readonly #window = inject(WINDOW);
  readonly #tenantRefService = inject(TenantRefService);
  readonly #tenantSwitcherService = inject(TenantSwitcherService);

  readonly #keepKeys = [
    'locale',
    'mobileSidebarExpanded',
    'sidebarExpanded',
    'user',
    'tenantRef',
    'refresh_token',
    'token',
  ];

  // Tour dismissal is per page (`tourSeen:<pageKey>`), so the wipe needs a prefix match.
  readonly #keepKeyPrefixes = ['tourSeen:'];

  constructor() {
    this.user = this.#authService.getUser();
    this.mobileSidebarExpanded = this.#localStorageService.getItem('mobileSidebarExpanded') || false;
    this.currentLanguage = this.#localStorageService.getItem('locale') || this.#defaultLanguage;
    this.languages = [
      {
        value: 'en',
        label: this.#translateService.instant('locale.en'),
      },
      {
        value: 'nl',
        label: this.#translateService.instant('locale.nl'),
      },
    ];

    effect(() => {
      const tenantSwitcherEnabled = this.#tenantSwitcherService.getTenantSwitcherEnabled()();
      const pageConfig = this.#pageConfigService.pageConfig();

      if (!tenantSwitcherEnabled) {
        this.form.get('tenantRef')?.disable();
      } else {
        this.form.get('tenantRef')?.enable();
      }

      if (pageConfig?.sidebarExpanded !== undefined) {
        this.mobileSidebarExpanded = pageConfig.sidebarExpanded;
      }
    });
  }

  ngOnInit(): void {
    this.menuItems = this.#getMenuItems();
    this.form = this.#formBuilder.group({
      currentLanguage: [this.currentLanguage],
      tenantRef: [this.#localStorageService.getItem('tenantRef') || ''],
    });
  }

  executeCommand(item: MenuItem, event: MouseEvent): void {
    if (item.clickable) {
      if (item.command) {
        item.command(null);
      } else if (item.routerLink) {
        this.#router.navigateByUrl(item.routerLink);
      }

      this.menuPanel().hide();
    } else {
      event.preventDefault();
    }
  }

  openMobileSidebar(event: MouseEvent): void {
    event.stopPropagation();
    event.preventDefault();

    this.mobileSidebarExpanded = !this.mobileSidebarExpanded;

    this.#pageConfigService.patchPageConfig({
      sidebarExpanded: this.mobileSidebarExpanded,
    });
  }

  changeLanguage(event: DropdownChangeEvent): void {
    this.#translateService.use(event.value);
    this.currentLanguage = event.value;
    this.#localStorageService.setItem('locale', event.value);
    this.menuPanel().hide();
    this.#reloadCurrentRoute();
  }

  /**
   * Re-enters the current route so its templates re-render in the new language.
   *
   * Driven from `changeLanguage` rather than from `TranslateService.onLangChange`: that event is
   * global and `use()` emits it unconditionally, so `translationResolver` fired it on every
   * navigation into `/auth` — which meant logging out started this dance mid-flight and
   * navigated the user straight back to the page they came from, cancelling the login
   * navigation (NavigationCancel, SupersededByNewNavigation).
   */
  #reloadCurrentRoute(): void {
    this.#loadingService.toggleLoading(true);

    const url = this.#router.url;

    from(this.#router.navigateByUrl('/', { skipLocationChange: true }))
      .pipe(
        switchMap(() => {
          return this.#router.navigate([url]);
        }),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe(() => {
        this.#feedbackWidgetService.reloadFeedbackWidget();
        this.#loadingService.hideLoader();
      });
  }

  switchTenant(event: DropdownChangeEvent): void {
    this.#loadingService.toggleLoading(true);
    this.#tenantRefService.setTenantRef(event.value);
    this.#window.location.reload();
    this.#removeUnusedLocalStorageItems();
  }

  #getMenuItems(): MenuItem[] {
    return [
      {
        label: 'link.profile',
        icon: 'mynaui-user-circle',
        clickable: true,
        command: async () => {
          const userProfileUrl = [AppRoutes.user, AppRoutes.profile];
          const backUrl = this.#router.url;

          if (!this.#router.url.includes(userProfileUrl.join('/'))) {
            await this.#router.navigate(userProfileUrl);
            this.#pageConfigService.patchPageConfig({
              backUrl,
            });
          }
        },
      },
      {
        label: 'config.manage.title',
        icon: 'mynaui-cog',
        routerLink: `/${AppRoutes.config}`,
        requiredPermission: 'module.manage.tenant', // @TODO: Find a permission only admins have
        clickable: true,
      },
      {
        label: '',
        icon: 'mynaui-home',
        clickable: false,
        type: MenuItemType.Template,
        key: 'tenant',
      },
      {
        label: 'link.logout',
        icon: 'mynaui-login',
        clickable: true,
        command: () => {
          this.#authService
            .logout()
            .pipe(
              first(),
              finalize(() => {
                this.#authService.tokens.forEach((key) => {
                  localStorage.removeItem(key);
                });

                this.#router.navigate(['/', AppRoutes.auth, AppRoutes.login]);
              }),
            )
            .subscribe();
        },
      },
      {
        label: '',
        icon: '',
        clickable: false,
        type: MenuItemType.Template,
        key: 'language',
      },
    ];
  }

  #removeUnusedLocalStorageItems(): void {
    Object.keys(localStorage).forEach((key) => {
      if (!this.#keepKeys.includes(key) && !this.#keepKeyPrefixes.some((prefix) => key.startsWith(prefix))) {
        localStorage.removeItem(key);
      }
    });
  }
}
